import React, { useEffect, useState } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import {
    Alert,
    Button,
    Form,
    FormField,
    Input,
    Multiselect,
    SpaceBetween,
} from "@cloudscape-design/components";
import {
    setPageTitle,
    appendInfoMessage,
    selectOnboardingRequest,
    removeInfoMessage,
    resetOnboardingRequest,
} from "src/core/redux";
import { SentinelPageProps } from "src/web/interfaces";
import { SentinelBasePage } from "src/web/pages/sentinel-base";
import { submitOnboardingRequest, getUserPipelines } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { useSelector } from "react-redux";
import { SentinelLoadingStatus } from "src/core/sentinel/types";
import { useInfoSection } from "src/web/components/SentinelLayout/index";
import { SentinelDefaultInfoSection } from "src/web/components/SentinelLayout/help/default-info-section";

export interface SentinelOnboardingPageProps extends SentinelPageProps {
    readOnly?: boolean;
    sentinelController: SentinelController;
}

export const OnboardingPage: React.FC<SentinelOnboardingPageProps> = (
    props: SentinelOnboardingPageProps
) => {
    const [, setInfoSection,,setToolsOpen] = useInfoSection();
    const [alias, setAlias] = useState("");
    const [teamName, setTeamName] = useState("");
    const [teamEmail, setTeamEmail] = useState("");
    const [selectedPipelines, setSelectedPipelines] = useState<any[]>([]);
    const [availablePipelines, setAvailablePipelines] = useState<any[]>([]);
    const [pipelinesLoading, setPipelinesLoading] = useState(true);
    const [pipelinesError, setPipelinesError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const onboardingRequest = useSelector(selectOnboardingRequest);
    const status = onboardingRequest?.status;
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setPageTitle("Onboarding Page"));
    }, []);

    useEffect(() => {
        setAlias(props.sentinelController.userIdentity.username);
    }, [props.sentinelController.userIdentity.username]);

    useEffect(() => {
        const fetchPipelines = async () => {
            if (!props.sentinelController.userIdentity.username) {
                return;
            }
            setPipelinesLoading(true);
            setPipelinesError("");
            try {
                const response = await dispatch(
                    getUserPipelines({
                        sentinelController: props.sentinelController,
                        userId: props.sentinelController.userIdentity.username,
                    })
                ).unwrap();
                const pipelineNames = (response as any)?.payload?.pipelineNames ?? [];
                setAvailablePipelines(
                    pipelineNames.map((name: string) => ({
                        label: name,
                        value: name,
                    }))
                );
            } catch (error) {
                setPipelinesError("Failed to load pipelines. Please try again.");
            } finally {
                setPipelinesLoading(false);
            }
        };
        fetchPipelines();
    }, [props.sentinelController.userIdentity.username]);

    useEffect(() => {
        return () => {
            setToolsOpen(false);
            setInfoSection(<SentinelDefaultInfoSection />);
        };
    }, []);

    useEffect(() => {
        dispatch(removeInfoMessage({ id: "message_1" }));
    }, []);

    useEffect(() => {
        if (status === SentinelLoadingStatus.FINISHED) {
            dispatch(removeInfoMessage({ id: "message_onboardingrequest_submit" }));
            dispatch(appendInfoMessage({
                type: "success",
                dismissible: true,
                content: "Request submitted",
                dismissLabel: "Dismiss",
                id: "message_onboardingrequest_success",
            }));
        }
    }, [status]);

    const pipelineNames = selectedPipelines.map((p) => p.value);
    const isEmailValid = teamEmail === "" || /^[a-zA-Z0-9._%+-]+@amazon.com$/.test(teamEmail);
    const isFormValid = teamName !== "" && pipelineNames.length > 0 && isEmailValid;

    const handleSubmit = async () => {
        setSubmitting(true);
        const payload = {
            requesterId: alias,
            pipelineNames: pipelineNames,
            teamName: teamName,
            teamEmail: teamEmail,
        };
        dispatch(appendInfoMessage({
            type: "info",
            loading: true,
            content: "Request submission in progress",
            dismissLabel: "Dismiss",
            id: "message_onboardingrequest_submit",
        }));
        try {
            await dispatch(
                submitOnboardingRequest({
                    sentinelController: props.sentinelController,
                    onboardingRequestInput: payload,
                })
            ).unwrap();
            setSubmitting(false);
            setTeamName("");
            setTeamEmail("");
            setSelectedPipelines([]);
        } catch (error) {
            setSubmitting(false);
            dispatch(removeInfoMessage({ id: "message_onboardingrequest_submit" }));
            dispatch(appendInfoMessage({
                type: "error",
                dismissible: true,
                content: "Request submission failed. Please try again.",
                dismissLabel: "Dismiss",
                id: "message_onboardingrequest_error",
            }));
            dispatch(resetOnboardingRequest());
        }
    };

    return (
        <SentinelBasePage>
            <Form
                actions={
                    <SpaceBetween direction="vertical" size="s">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button
                                variant="primary"
                                disabled={!isFormValid || submitting}
                                onClick={() => handleSubmit()}
                            >
                                Submit Request
                            </Button>
                        </SpaceBetween>
                    </SpaceBetween>
                }
            >
                <SpaceBetween size="l">
                    <FormField>
                        <div className="formfield-margin">
                            <strong>Requester:</strong> {alias}
                        </div>
                    </FormField>

                    <FormField label="Team Name *">
                        <Input
                            type="text"
                            placeholder="Enter Team Name as per Teams record (https://permissions.amazon.com/)"
                            value={teamName}
                            onChange={(event) => setTeamName(event.detail.value)}
                        />
                    </FormField>

                    <FormField label="Team Email" errorText={teamEmail && !isEmailValid ? "Enter a valid @amazon.com email" : ""}>
                        <Input
                            type="text"
                            placeholder="Enter team email"
                            value={teamEmail}
                            onChange={(event) => setTeamEmail(event.detail.value)}
                        />
                    </FormField>

                    <FormField
                        label="Pipeline Name *"
                        description="Select pipelines you have access to"
                    >
                        {pipelinesError && (
                            <Alert type="error">{pipelinesError}</Alert>
                        )}
                        <Multiselect
                            selectedOptions={selectedPipelines}
                            onChange={({ detail }) => setSelectedPipelines([...detail.selectedOptions])}
                            options={availablePipelines}
                            filteringType="auto"
                            placeholder="Search and select pipelines"
                            loadingText="Loading pipelines..."
                            statusType={pipelinesLoading ? "loading" : "finished"}
                            
                            tokenLimit={5}
                        />
                    </FormField>
                </SpaceBetween>
            </Form>
        </SentinelBasePage>
    );
};
